import { useMemo } from 'react';

import { View } from 'native-base';
import { StyleSheet, Text } from 'react-native';

import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';

import { usePaymentContext } from '../../../../../../features/payment';
import { CardTypes, IPaymentMethod } from '../../../../../../features/payment/types';
import { mapPaymentMethodCardTypes } from '../../../../../../features/payment/utils';

const useAltPaymentMethodName = (paymentMethod: IPaymentMethod | undefined) => {
  const altPaymentMethodName = useMemo(() => {
    const altPaymentMethodsMap: { name: string; isSelected: boolean }[] = [
      {
        name: t('cash'),
        isSelected: !!paymentMethod?.cash,
      },
      {
        name: t('paypal'),
        isSelected: !!paymentMethod?.paypal,
      },
      {
        name: t('venmo'),
        isSelected: !!paymentMethod?.venmo,
      },
      {
        name: t('cashAppPay'),
        isSelected: !!paymentMethod?.cashapp,
      },
    ];

    const altPaymentMethod = altPaymentMethodsMap.find(
      altPaymentMethod => altPaymentMethod.isSelected
    );

    return altPaymentMethod?.name;
  }, [paymentMethod]);

  return altPaymentMethodName;
};

type PaymentMethodDetailsProps = {
  paymentMethod: IPaymentMethod | undefined;
  displayFor?: 'selection' | 'selectorOption';
};
export function PaymentMethodDetails({
  paymentMethod,
  displayFor = 'selection',
}: PaymentMethodDetailsProps) {
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const altPaymentMethodName = useAltPaymentMethodName(paymentMethod);

  const isCard =
    !!paymentMethod?.credit &&
    paymentMethod?.credit?.cardType !== CardTypes.APPLE_PAY &&
    paymentMethod?.credit?.cardType !== CardTypes.GOOGLE_PAY;

  const isWallet =
    !!paymentMethod?.credit &&
    (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY ||
      paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY);

  const cardType = mapPaymentMethodCardTypes(paymentMethod?.credit?.cardType);

  const containerStyle = StyleSheet.compose(styles.container, {
    flexDirection: displayFor === 'selection' ? 'column' : 'row',
    gap: displayFor === 'selection' ? 0 : 1,
    justifyContent: 'center',
  });

  const cardTypeStyle = StyleSheet.compose(styles.cardType, {
    fontFamily:
      displayFor === 'selection' ? primitive.fontFamily.$headers : primitive.fontFamily.$body,
    fontSize: displayFor === 'selection' ? 18 : 15,
    fontWeight: displayFor === 'selection' ? 'normal' : 'light',
  });

  let alias = paymentMethod?.credit?.alias;
  if (paymentMethod?.credit?.cardType === CardTypes.APPLE_PAY) {
    alias = t('applePay');
  }
  if (paymentMethod?.credit?.cardType === CardTypes.GOOGLE_PAY) {
    alias = t('googlePay');
  }

  if (isCard) {
    return (
      <View style={containerStyle}>
        <View style={{ flexDirection: 'row', gap: primitive.spacing.$1 }}>
          <Text style={cardTypeStyle} testID="payment-method-card">
            {cardType || t('card')}
          </Text>
          <Text style={cardTypeStyle} testID="payment-method-alias">
            {t('endsInRedesign').toLowerCase()}
            {paymentMethod?.credit?.alias}
          </Text>
        </View>
        {displayFor === 'selectorOption' && <Text style={styles.cardExp}>-</Text>}
        <Text style={styles.cardExp}>
          {`${t('expire')} ${paymentMethod?.credit?.expiryMonth}/${
            paymentMethod?.credit?.expiryYear
          }`}
        </Text>
      </View>
    );
  }

  if (isWallet) {
    return (
      <View style={containerStyle}>
        <Text style={cardTypeStyle} testID="payment-method-card">
          {alias}
        </Text>
      </View>
    );
  }

  if (paymentMethod?.prepaid) {
    return (
      <View style={[containerStyle, displayFor !== 'selection' && { alignItems: 'center' }]}>
        <Text style={cardTypeStyle}>{t('giftCard')}</Text>
        {displayFor !== 'selection' && <Text style={cardTypeStyle}> - </Text>}
        <Currency
          amount={getBalanceFromPaymentMethods(paymentMethod)}
          textProps={{
            margin: 0,
          }}
        />
      </View>
    );
  }

  // If we reached to this point we will be displaying an alternative payment method
  return (
    <View style={containerStyle}>
      <Text style={cardTypeStyle}>{altPaymentMethodName}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
  },
  cardContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: primitive.spacing.$1,
  },
  cardType: {
    color: primitive.bk.$bbqBrown,
    fontSize: 18,
    fontWeight: 'normal',
  },
  cardAlias: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    marginLeft: primitive.spacing.$1,
  },
  cardExp: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    textTransform: 'capitalize',
  },
});
